import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { activity as seedActivity, reports as seedReports, seedLoops } from './seed'
import type { ActivityItem, Draft, Loop, RemixEdge, Report } from './types'
import { deleteDraft, persistDraft } from './db'

interface AppState {
  currentUserId: string
  loops: Loop[]
  drafts: Draft[]
  liked: string[]
  saved: string[]
  followed: string[]
  joinedScenes: string[]
  muted: boolean
  activity: ActivityItem[]
  reports: Report[]
  hidden: string[]
  blockedProfiles: string[]
  mutedProfiles: string[]
  remixEdges: RemixEdge[]
  reducedMotion: boolean
  captionsAlways: boolean
  privateAccount: boolean
  sensitiveBlur: boolean
  likeLoop: (id: string) => void
  saveLoop: (id: string) => void
  follow: (id: string) => void
  joinScene: (id: string) => void
  setMuted: (value: boolean) => void
  saveDraft: (draft: Draft) => void
  publishLoop: (loop: Loop) => void
  submitReport: (loopId: string, reason: string, details?: string) => void
  resolveReport: (id: string, action: string) => void
  hideLoop: (id: string) => void
  blockProfile: (id: string) => void
  muteProfile: (id: string) => void
  addRemixEdge: (edge: RemixEdge) => void
  setReducedMotion: (value: boolean) => void
  setCaptionsAlways: (value: boolean) => void
  setPrivateAccount: (value: boolean) => void
  setSensitiveBlur: (value: boolean) => void
}

export const useAppStore = create<AppState>()(persist((set) => ({
  currentUserId: 'p-you', loops: seedLoops, drafts: [], liked: [], saved: [], followed: ['p-mina', 'p-sol'], joinedScenes: [], muted: true, activity: seedActivity, reports: seedReports, hidden: [], blockedProfiles: [], mutedProfiles: [], remixEdges: [], reducedMotion: false, captionsAlways: true, privateAccount: false, sensitiveBlur: true,
  likeLoop: (id) => set((state) => ({ liked: state.liked.includes(id) ? state.liked.filter((item) => item !== id) : [...state.liked, id], loops: state.loops.map((loop) => loop.id === id ? { ...loop, likes: loop.likes + (state.liked.includes(id) ? -1 : 1) } : loop) })),
  saveLoop: (id) => set((state) => ({ saved: state.saved.includes(id) ? state.saved.filter((item) => item !== id) : [...state.saved, id], loops: state.loops.map((loop) => loop.id === id ? { ...loop, saves: loop.saves + (state.saved.includes(id) ? -1 : 1) } : loop) })),
  follow: (id) => set((state) => ({ followed: state.followed.includes(id) ? state.followed.filter((item) => item !== id) : [...state.followed, id] })),
  joinScene: (id) => set((state) => ({ joinedScenes: state.joinedScenes.includes(id) ? state.joinedScenes : [...state.joinedScenes, id] })),
  setMuted: (muted) => set({ muted }),
  saveDraft: (draft) => { void persistDraft(draft); set((state) => ({ drafts: [...state.drafts.filter((item) => item.id !== draft.id), draft] })) },
  publishLoop: (loop) => set((state) => { const matchingDraft = state.drafts.find((draft) => draft.mediaUrl === loop.segments[0]?.mediaUrl); if (matchingDraft) void deleteDraft(matchingDraft.id); return { loops: [loop, ...state.loops], drafts: matchingDraft ? state.drafts.filter((draft) => draft.id !== matchingDraft.id) : state.drafts } }),
  submitReport: (loopId, reason, details) => set((state) => ({ reports: [{ id: crypto.randomUUID(), loopId, reason, details, status: 'open', reporterCount: 1, createdAt: new Date().toISOString(), audit: [] }, ...state.reports] })),
  resolveReport: (id, action) => set((state) => ({ reports: state.reports.map((report) => report.id === id ? { ...report, status: 'actioned', audit: [...report.audit, { action, at: new Date().toISOString(), actor: 'Mara Chen' }] } : report) })),
  hideLoop: (id) => set((state) => ({ hidden: [...new Set([...state.hidden, id])] })),
  blockProfile: (id) => set((state) => ({ blockedProfiles: [...new Set([...state.blockedProfiles, id])], loops: state.loops.filter((loop) => loop.creatorId !== id) })),
  muteProfile: (id) => set((state) => ({ mutedProfiles: [...new Set([...state.mutedProfiles, id])] })),
  addRemixEdge: (edge) => set((state) => ({ remixEdges: [...state.remixEdges.filter((item) => item.childLoopId !== edge.childLoopId), edge] })),
  setReducedMotion: (reducedMotion) => set({ reducedMotion }),
  setCaptionsAlways: (captionsAlways) => set({ captionsAlways }),
  setPrivateAccount: (privateAccount) => set({ privateAccount }),
  setSensitiveBlur: (sensitiveBlur) => set({ sensitiveBlur })
}), { name: 'loop6-state-v4', version: 4 }))
